/**
 * 편집기 툴바 **오른편** — 세 레이아웃(붙임 · 편집기 분리 · 결과 분리)이 이 컴포넌트 하나를 쓴다.
 *
 * 예전엔 세 곳에 거의 같은 코드가 복사돼 있어, 순서·모양을 한 곳만 고치는 일이 생겼다.
 * 배치 규칙은 docs/ui-command-placement.md §3 — 편집기에 가까운 것 → 앱 전체에 가까운 것 순서:
 *
 *   [오른쪽 패널] │ [도구 창 · 즐겨찾기한 창 메뉴]
 *
 * 편집기에 작용하는 것(컬럼 선택·주석 보기·수직 분할·결과창 닫기·창 분리)은 왼쪽의 EditorOptionChecks 가 맡는다
 * (2026-10-05 — 결과창 닫기·창 분리를 수직 분할 옆으로 옮겼다. 줄의 왼쪽은 **편집기의 것**, 여기부터는 앱의 것이다).
 *
 * 옵션(편집기 속성)은 체크박스, 모드·패널(화면 구성이 바뀜)은 눌림 상태가 보이는 버튼이다.
 * 추천 단어(자동완성)는 한 번 정하는 선호라 여기 두지 않고 설정 › SQL 에디터에만 둔다.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiPieChart, FiMessageSquare, FiClock, FiAward, FiCheckCircle, FiXCircle, FiAlertCircle } from 'react-icons/fi';
import AssistantToolButtons from './assistant/AssistantToolButtons';
import { AI_ENABLED } from '../config/features';

/**
 * 편집기 옵션 체크박스 — 컬럼 선택 · 주석 보기.
 *
 * 편집기 자체에 작용하는 옵션이라 툴바 **왼쪽**, 편집 아이콘 묶음 옆에 둔다(사용자 요청 2026-09-13).
 * 수직 분할도 편집기를 보는 방식이라 여기 함께 둔다.
 * 예전엔 오른편 묶음의 맨 앞에 있어 화면 배치·패널 버튼과 한데 섞여 보였다.
 */
export function EditorOptionChecks({
    editorOptions, setEditorOptions, showChecks = true, isEditorSplit, onToggleSplit,
    // 결과창 닫기·창 분리 — 편집기 화면 배치라 수직 분할 옆에 붙인다(사용자 요청 2026-10-05)
    collapseMode, onToggleCollapse, isEditorDetached, onToggleDetach,
}) {
    const { t } = useTranslation();
    return (
        <div className="editor-action-subgroup">
            {showChecks && (<>
            <label className="editor-option-label">
                <input type="checkbox" checked={!!editorOptions.columnSelection} onChange={(e) => setEditorOptions(p => ({ ...p, columnSelection: e.target.checked }))} />
                {/* 체크박스 · 레이블 — **체크박스가 있으면 아이콘을 두지 않는다**(사용자 요청 2026-10-05). 결과 툴바 체크박스와 같은 모양 */}
                {t('common:tb.columnSelect')} <span style={{ fontSize: '10px', color: '#999', marginLeft: 4 }}>{t('common:ui.altDrag')}</span>
            </label>
            <label className="editor-option-label">
                <input type="checkbox" checked={!!editorOptions.showComments} onChange={(e) => setEditorOptions(p => ({ ...p, showComments: e.target.checked }))} />
                {t('common:tb.showCommentsShort')} <span style={{ fontSize: '10px', color: '#999', marginLeft: 4 }}>(F2)</span>
            </label>
            </>)}
            {/* 수직 분할 · 창 분리 · 결과창 닫기 — 편집기 화면 배치(순서는 사용자 요청 2026-10-05). **버튼이 아니라 체크박스**다(사용자 요청 2026-10-05):
                켜짐/꺼짐이 상태라 옵션과 같은 모양이어야 한다. 레이블은 고정 — 「수직분할취소」 처럼 반대말로 바꾸지 않는다(체크가 상태를 말한다) */}
            {onToggleSplit && (
                <label className="editor-option-label" title={isEditorSplit ? t('common:ui.mergeVertical') : t('common:ui.splitVertical')}>
                    <input type="checkbox" checked={!!isEditorSplit} onChange={onToggleSplit} />
                    {t('common:ui.splitVerticalShort')}
                </label>
            )}
            {onToggleDetach && (
                <label className="editor-option-label" title={isEditorDetached ? t('common:ui.reattach') : t('common:ui.detachEditor')}>
                    <input type="checkbox" checked={!!isEditorDetached} onChange={onToggleDetach} />
                    {t('common:tb.detach')}
                </label>
            )}
            {onToggleCollapse && (
                <label className="editor-option-label" title={collapseMode === 'bottom' ? t('common:ui.expandResult') : t('common:ui.collapseResult')}>
                    <input type="checkbox" checked={collapseMode === 'bottom'} onChange={onToggleCollapse} />
                    {t('common:tb.hideResult')}
                </label>
            )}
        </div>
    );
}

/**
 * 결과 영역의 화면 배치 — 수직 분할 · 창 분리 체크박스(2026-10-05, 버튼에서 바꿈).
 * 결과 툴바는 레이아웃마다 따로 그려져(붙임·편집기 분리·결과 분리) 세 곳이 이 컴포넌트를 쓴다 — 베끼면 한 곳만 고쳐진다.
 */
export function ResultLayoutChecks({ isResultSplit, onToggleSplit, isResultDetached, onToggleDetach }) {
    const { t } = useTranslation();
    return (
        <>
            <label className="compact-view-toggle" title={isResultSplit ? t('common:ui.mergeResult') : t('common:ui.splitResult')}>
                <input type="checkbox" checked={!!isResultSplit} onChange={onToggleSplit} />
                {t('common:ui.splitVerticalShort')}
            </label>
            <label className="compact-view-toggle" title={isResultDetached ? t('common:ui.reattach') : t('common:ui.detachResult')}>
                <input type="checkbox" checked={!!isResultDetached} onChange={onToggleDetach} />
                {t('common:tb.detach')}
            </label>
        </>
    );
}

ResultLayoutChecks.propTypes = {
    isResultSplit: PropTypes.bool,
    onToggleSplit: PropTypes.func.isRequired,
    isResultDetached: PropTypes.bool,
    onToggleDetach: PropTypes.func.isRequired,
};

/**
 * 트랜잭션 칩 — 활성 탭의 세션에 **미커밋 변경**이 있을 때만 보인다(세 레이아웃이 같은 컴포넌트, 2026-10-03).
 * 커밋·롤백은 그 세션의 커넥션에 간다(`/sessions/:id/commit`). 즐겨찾기와 무관하게 늘 보여야 한다 —
 * 변경이 떠 있는데 커밋 단추가 안 보이면 탭을 닫는 순간 롤백된다.
 */
export function TxnChip({ session, onCommit, onRollback, busy }) {
    const { t } = useTranslation();
    if (!session || !session.uncommitted) return null;
    return (
        <div className="editor-action-subgroup txn-chip" title={t('common:sess.uncommittedHint')}>
            <span className="txn-chip-label"><FiAlertCircle size={12} /> {t('common:sess.uncommitted')}</span>
            <button className="detach-inline-btn txn-commit" onClick={onCommit} disabled={busy} title={t('common:sess.commitHint')}>
                <FiCheckCircle /> {t('common:sess.commit')}
            </button>
            <button className="detach-inline-btn txn-rollback" onClick={onRollback} disabled={busy} title={t('common:sess.rollbackHint')}>
                <FiXCircle /> {t('common:sess.rollback')}
            </button>
        </div>
    );
}

TxnChip.propTypes = {
    session: PropTypes.object,
    onCommit: PropTypes.func,
    onRollback: PropTypes.func,
    busy: PropTypes.bool,
};

EditorOptionChecks.propTypes = {
    editorOptions: PropTypes.object,
    setEditorOptions: PropTypes.func,
    // false 면 체크박스를 숨기고 수직 분할만 — 결과 분리 레이아웃은 원래 체크박스가 없었다
    showChecks: PropTypes.bool,
    isEditorSplit: PropTypes.bool,
    onToggleSplit: PropTypes.func,
    collapseMode: PropTypes.string,
    onToggleCollapse: PropTypes.func,
    isEditorDetached: PropTypes.bool,
    onToggleDetach: PropTypes.func,
};

/**
 * 오른쪽 패널에 펼칠 화면들. 순서·문구는 예전 탭과 같게 둔다 — 자리만 옮긴 것이지
 * 다른 기능이 된 것이 아니다.
 */
const SIDE_PANEL_VIEWS = [
    { id: 'history', labelKey: 'common:hct.history', icon: <FiClock /> },
    { id: 'challenge', labelKey: 'common:hct.challenge', icon: <FiAward /> },
];

function EditorToolbarRight({
    analysisMode, assistantOpen, onToggleAnalysis, onToggleAssistant,
    // 오른쪽 패널에 무엇을 펼칠지 — null 이면 닫힘. 같은 것을 다시 누르면 닫는다.
    sidePanelView, onToggleSidePanelView,
    onOpenCaseList, onOpenTableAnalysis,
    windowActions,
    isToolVisible,
}) {
    const { t } = useTranslation();
    const visible = (id) => (isToolVisible ? isToolVisible(id) : true);
    // 도구 창 — 메뉴 도구 › 의 즐겨찾기를 끄면 숨는다(utils/actionFavorites.js FIXED_PLACEMENTS).
    // 사용·에러 로그는 메뉴 도움말 › 에만 있다(도움말 항목은 툴바 버튼을 두지 않는다, 2026-09-13)
    const tools = {
        // AI 제외 빌드에는 두 창이 없다(config/features.js)
        caseList: AI_ENABLED && visible('tool-tuning-cases'),
        // 툴바 버튼은 인덱스 설계 창(테이블 액세스 분석 탭 포함, 2026-10-15) — 메뉴의 그 항목 즐겨찾기를 따른다
        tableAnalysis: AI_ENABLED && visible('tool-index-design'),
    };
    // 즐겨찾기한 "창을 여는 메뉴"(모니터링·연결·설정) — App.renderActionGroup({ windows: true }) 가 버튼을 만든다
    const windowButtons = Array.isArray(windowActions) ? windowActions.filter(Boolean) : [];
    const anyTool = tools.caseList || tools.tableAnalysis || windowButtons.length > 0;

    return (
        <>
            {/* 오른쪽 패널 — 함께 열 수 있다. AI 에이전트가 늘 맨 오른쪽이고 분석 패널은 그 왼쪽으로 밀린다.
                여기부터가 줄의 **앱 쪽**이라 오른쪽으로 민다(right-align) */}
            <div className="editor-action-subgroup right-align">
                <button
                    className={`detach-inline-btn${analysisMode ? ' active' : ''}`}
                    onClick={onToggleAnalysis}
                    title={t('common:ui.analysisToggle')}
                    aria-pressed={!!analysisMode}
                >
                    <FiPieChart /> {t('common:tb.analysisMode')}
                </button>
                {onToggleAssistant && (
                    <button
                        className={`detach-inline-btn${assistantOpen ? ' active' : ''}`}
                        onClick={onToggleAssistant}
                        title={t('common:ui.assistantToggle')}
                        aria-pressed={!!assistantOpen}
                    >
                        <FiMessageSquare /> {t('common:tb.assistant')}
                    </button>
                )}
                {/* SQL 튜닝 · 실행이력 · 쿼리 챌린지 — **패널 안에서 또 탭으로 나뉘지 않는다**
                    (사용자 요청 2026-09-20). 예전에는 패널을 연 뒤 그 안에서 탭을 한 번 더 골라야
                    해서, 열려 있는 것이 무엇인지 두 곳을 봐야 알 수 있었다. 이제 여기서 고른 것이
                    곧 펼쳐지는 화면이고, 눌린 버튼이 지금 열린 것을 말해 준다. */}
                {onToggleSidePanelView && SIDE_PANEL_VIEWS.map(v => (
                    <button
                        key={v.id}
                        className={`detach-inline-btn${sidePanelView === v.id ? ' active' : ''}`}
                        onClick={() => onToggleSidePanelView(v.id)}
                        title={t(v.labelKey)}
                        aria-pressed={sidePanelView === v.id}
                    >
                        {v.icon} {t(v.labelKey)}
                    </button>
                ))}
            </div>

            {anyTool && (
                <>
                    {/* 도구 창은 **늘 둘째 줄**에서 시작한다(2026-10-16 사용자 요청) — 폭에 따라 한 줄·두 줄을 오가면 단추 자리가 바뀐다 */}
                    <span className="editor-toolbar-break" aria-hidden="true" />
                    {/* 도구 창 — 대화 없이 혼자 쓰는 화면이라 패널이 아니라 여기 둔다(사용자 결정 2026-09-11). 둘째 줄의 **오른쪽 끝**(첫 줄 오른쪽 단추들 아래) */}
                    <div className="editor-action-subgroup right-align">
                        <AssistantToolButtons
                            onOpenCaseList={onOpenCaseList}
                            onOpenTableAnalysis={onOpenTableAnalysis}
                            visible={tools}
                        />
                        {windowButtons}
                    </div>
                </>
            )}
        </>
    );
}

EditorToolbarRight.propTypes = {
    analysisMode: PropTypes.bool,
    assistantOpen: PropTypes.bool,
    onToggleAnalysis: PropTypes.func.isRequired,
    // AI 제외 빌드에서는 넘기지 않는다 — 없으면 에이전트 버튼을 그리지 않는다
    onToggleAssistant: PropTypes.func,
    onOpenCaseList: PropTypes.func.isRequired,
    onOpenTableAnalysis: PropTypes.func.isRequired,
    windowActions: PropTypes.node,
    isToolVisible: PropTypes.func,
};

export default EditorToolbarRight;
